import { useEffect, useRef, useState } from "react";

const SUPPORT_CONFIG = {
  creatorLabel: import.meta.env.VITE_SUPPORT_CREATOR_LABEL || "Support the builder",
  creatorUrl: import.meta.env.VITE_SUPPORT_CREATOR_URL || "",
  causeLabel: import.meta.env.VITE_SUPPORT_CAUSE_LABEL || "Support a cause",
  causeUrl: import.meta.env.VITE_SUPPORT_CAUSE_URL || "",
  note:
    import.meta.env.VITE_SUPPORT_NOTE ||
    "Wire your live support and mission links into VITE_SUPPORT_CREATOR_URL and VITE_SUPPORT_CAUSE_URL.",
};

function SupportAction({ href, label }: { href: string; label: string }) {
  if (!href) {
    return (
      <span className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/45">
        <span>{label}</span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/35">set link</span>
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
    >
      <span>{label}</span>
      <span aria-hidden="true">↗</span>
    </a>
  );
}

export default function SupportDock() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, []);

  return (
    <div ref={panelRef} className="fixed bottom-4 right-4 z-[70] flex flex-col items-end gap-3">
      {open ? (
        <div className="w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-white/12 bg-[rgba(8,14,24,0.88)] p-4 shadow-[0_20px_55px_rgba(0,0,0,0.42)] backdrop-blur-xl">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/45">Support</div>
              <div className="mt-1 text-base font-semibold text-white">Keep this lane alive</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/60 transition hover:bg-white/10 hover:text-white"
              aria-label="Close support menu"
            >
              Close
            </button>
          </div>
          <div className="grid gap-2">
            <SupportAction href={SUPPORT_CONFIG.creatorUrl} label={SUPPORT_CONFIG.creatorLabel} />
            <SupportAction href={SUPPORT_CONFIG.causeUrl} label={SUPPORT_CONFIG.causeLabel} />
          </div>
          <p className="mt-3 text-xs leading-5 text-white/45">{SUPPORT_CONFIG.note}</p>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="grid h-12 w-12 place-items-center rounded-full border border-white/12 bg-[rgba(8,14,24,0.88)] text-white shadow-[0_16px_40px_rgba(0,0,0,0.38)] backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-white/22 hover:bg-[rgba(10,18,30,0.94)]"
        aria-label="Open support links"
        aria-expanded={open}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 20.2 4.8 13.4a4.62 4.62 0 0 1 0-6.7 4.9 4.9 0 0 1 6.84 0L12 7.04l.36-.34a4.9 4.9 0 0 1 6.84 0 4.62 4.62 0 0 1 0 6.7L12 20.2Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
